import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { CreditCard, Lock, MapPin, ShieldCheck, Truck, Gem } from "lucide-react";

const COUNTRIES = [
  ["CY", "Cyprus"], ["GR", "Greece"], ["DE", "Germany"], ["FR", "France"],
  ["IT", "Italy"], ["ES", "Spain"], ["NL", "Netherlands"], ["IE", "Ireland"],
  ["GB", "United Kingdom"], ["US", "United States"], ["CA", "Canada"],
  ["AU", "Australia"], ["AE", "United Arab Emirates"],
];

export default function Checkout() {
  const { items, total: subtotal, payload } = useCart();
  const { user } = useAuth();
  const [provider, setProvider] = useState("stripe");
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [redeem, setRedeem] = useState(0);
  const [rewardsInfo, setRewardsInfo] = useState(null);
  const navigate = useNavigate();

  const [addr, setAddr] = useState({
    full_name: user?.name || "", line1: "", line2: "", city: "",
    postal_code: "", country: "CY", phone: "",
  });
  const [saved, setSaved] = useState([]);
  const [saveAddr, setSaveAddr] = useState(false);

  useEffect(() => {
    api.get("/shop/addresses").then((r) => {
      setSaved(r.data);
      const def = r.data.find((a) => a.is_default) || r.data[0];
      if (def) {
        setAddr({
          full_name: def.full_name, line1: def.line1, line2: def.line2 || "",
          city: def.city, postal_code: def.postal_code, country: def.country, phone: def.phone || "",
        });
      }
    }).catch(() => {});
    api.get("/rewards/me").then((r) => setRewardsInfo(r.data)).catch(() => {});
  }, []);

  const applySaved = (a) => setAddr({
    full_name: a.full_name, line1: a.line1, line2: a.line2 || "",
    city: a.city, postal_code: a.postal_code, country: a.country, phone: a.phone || "",
  });

  const cartPayloadItems = payload;

  const fetchQuote = useCallback(async (country, redeemPoints) => {
    if (items.length === 0) return;
    try {
      const res = await api.post("/shop/quote", { items: cartPayloadItems, country, redeem_points: redeemPoints });
      setQuote(res.data);
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  useEffect(() => { fetchQuote(addr.country, redeem); }, [addr.country, redeem, fetchQuote]);

  const validAddr = addr.full_name && addr.line1 && addr.city && addr.postal_code && addr.country;
  const points = user?.points || 0;
  const maxRedeem = quote?.max_redeemable_points ?? Math.min(points, Math.round(subtotal * 100));

  const buildPayload = () => ({
    items: cartPayloadItems,
    origin_url: window.location.origin,
    shipping: addr,
    redeem_points: redeem,
  });

  const payStripe = async () => {
    if (!validAddr) { toast.error("Complete the shipping address"); return; }
    setBusy(true);
    try {
      if (saveAddr) { try { await api.post("/shop/addresses", addr); } catch (e) {} }
      const res = await api.post("/shop/checkout", buildPayload());
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  const payAmazon = async () => {
    if (!validAddr) { toast.error("Complete the shipping address"); return; }
    setBusy(true);
    try {
      if (saveAddr) { try { await api.post("/shop/addresses", addr); } catch (e) {} }
      const res = await api.post("/shop/amazon-pay/create", buildPayload());
      toast.warning(res.data.message || "Amazon Pay requires merchant credentials.");
      navigate("/shop/order/amazon-pending");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Amazon Pay unavailable");
    } finally {
      setBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-app flex flex-col items-center justify-center gap-4">
        <p className="text-slate-400">Nothing to check out.</p>
        <button onClick={() => navigate("/shop")} className="btn-hud border border-cyan/40 text-cyan px-6 py-2.5 text-sm hover:bg-cyan hover:text-app">Browse Depot</button>
      </div>
    );
  }

  const field = (key, label, opts = {}) => (
    <div className={opts.span ? "sm:col-span-2" : ""}>
      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}{opts.req && " *"}</label>
      <input
        value={addr[key]}
        onChange={(e) => setAddr({ ...addr, [key]: e.target.value })}
        data-testid={`addr-${key}`}
        className="mt-1.5 w-full bg-cyan/5 border-b border-cyan/25 focus:border-cyan text-slate-100 px-3 py-2.5 font-mono text-sm outline-none transition-colors"
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-app relative pt-12 pb-28" data-testid="checkout-page">
      <div className="absolute inset-0 hud-grid opacity-15" />
      <div className="relative mx-auto max-w-5xl px-5">
        <div className="font-mono text-[11px] tracking-[0.4em] text-cyan/70">SECURE TERMINAL</div>
        <h1 className="font-head text-4xl font-bold uppercase mt-2 mb-8">Checkout</h1>

        <div className="grid md:grid-cols-5 gap-6">
          {/* left: account + address + payment */}
          <div className="md:col-span-3 space-y-5">
            <div className="glass clip-chamfer p-5">
              <div className="font-mono text-[10px] tracking-widest text-slate-400 mb-2">UNIFIED ACCOUNT</div>
              <div className="flex items-center gap-2 text-slate-200">
                <ShieldCheck className="w-4 h-4 text-cyan" />
                <span className="font-head">{user?.name}</span>
                <span className="text-slate-500 text-sm">· {user?.email}</span>
              </div>
              <div className="font-mono text-[10px] text-slate-500 mt-1">Same identity as your simulation account · Tier: {user?.tier_name}</div>
            </div>

            {/* shipping address */}
            <div className="glass clip-chamfer p-5" data-testid="shipping-form">
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-cyan/70 mb-4">
                <Truck className="w-3.5 h-3.5" /> SHIPPING DESTINATION
              </div>

              {saved.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4" data-testid="saved-addresses">
                  {saved.map((a) => (
                    <button
                      key={a.address_id}
                      onClick={() => applySaved(a)}
                      data-testid={`saved-addr-${a.address_id}`}
                      className="font-mono text-[10px] tracking-wide px-3 py-2 border border-white/15 text-slate-300 hover:border-cyan hover:text-cyan transition-colors text-left"
                    >
                      {a.is_default && <span className="text-amber">★ </span>}
                      {a.city}, {a.country} · {a.postal_code}
                    </button>
                  ))}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                {field("full_name", "Full Name", { req: true, span: true })}
                {field("line1", "Address Line 1", { req: true, span: true })}
                {field("line2", "Address Line 2")}
                {field("city", "City", { req: true })}
                {field("postal_code", "Postal Code", { req: true })}
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Country *</label>
                  <select
                    value={addr.country}
                    onChange={(e) => setAddr({ ...addr, country: e.target.value })}
                    data-testid="addr-country"
                    className="mt-1.5 w-full bg-cyan/5 border-b border-cyan/25 focus:border-cyan text-slate-100 px-3 py-2.5 font-mono text-sm outline-none"
                  >
                    {COUNTRIES.map(([code, name]) => (
                      <option key={code} value={code} className="bg-app">{name}</option>
                    ))}
                  </select>
                </div>
                {field("phone", "Phone")}
              </div>

              <label className="flex items-center gap-2 mt-4 cursor-pointer" data-testid="save-address-toggle">
                <input type="checkbox" checked={saveAddr} onChange={(e) => setSaveAddr(e.target.checked)} className="accent-cyan" />
                <span className="font-mono text-[10px] tracking-widest text-slate-400">SAVE THIS ADDRESS TO MY ACCOUNT</span>
              </label>
            </div>

            {/* AETHER points redemption */}
            {points > 0 && (
              <div className="glass clip-chamfer p-5" data-testid="points-redeem">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-cyan/70">
                    <Gem className="w-3.5 h-3.5" /> REDEEM AETHER POINTS
                  </div>
                  <span className="font-mono text-[10px] text-slate-400">{points} AVAILABLE</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={maxRedeem}
                  step={100}
                  value={Math.min(redeem, maxRedeem)}
                  onChange={(e) => setRedeem(Number(e.target.value))}
                  data-testid="redeem-slider"
                  className="w-full accent-cyan"
                />
                <div className="flex items-center justify-between mt-2 font-mono text-[10px]">
                  <span className="text-slate-500">{quote?.points_redeemed ?? redeem} pts applied</span>
                  <span className="text-cyan" data-testid="redeem-discount">− ${(quote?.discount ?? 0).toFixed(2)}</span>
                </div>
                <p className="font-mono text-[9px] text-slate-600 mt-1">100 points = $1.00 off (capped at item subtotal)</p>
              </div>
            )}

            <div className="font-mono text-[10px] tracking-widest text-slate-400">PAYMENT METHOD</div>
            {[
              { id: "stripe", label: "Card / Stripe", desc: "Same processor as your subscription", icon: CreditCard, accent: "#00E5FF" },
              { id: "amazon_pay", label: "Amazon Pay", desc: "Requires merchant credentials to go live", icon: Lock, accent: "#FFB800" },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setProvider(m.id)}
                data-testid={`pay-method-${m.id}`}
                className={`w-full glass flex items-center gap-4 p-4 border text-left transition-all ${
                  provider === m.id ? "border-cyan box-glow-cyan" : "border-white/10 hover:border-cyan/40"
                }`}
              >
                <div className="w-9 h-9 flex items-center justify-center border" style={{ borderColor: m.accent + "66", color: m.accent }}>
                  <m.icon className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <div className="font-head uppercase tracking-wide text-slate-100">{m.label}</div>
                  <div className="font-mono text-[10px] text-slate-500">{m.desc}</div>
                </div>
                <div className={`w-4 h-4 rounded-full border-2 ${provider === m.id ? "bg-cyan border-cyan" : "border-slate-600"}`} />
              </button>
            ))}
          </div>

          {/* right: summary */}
          <motion.div initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} className="md:col-span-2 glass-strong clip-chamfer brackets p-6 h-fit md:sticky md:top-24">
            <div className="font-mono text-[10px] tracking-widest text-slate-400 mb-4">ORDER MANIFEST</div>
            <div className="space-y-3 max-h-52 overflow-y-auto pr-1">
              {items.map((it) => (
                <div key={it.key} className="flex items-center gap-3">
                  <img src={it.image} alt={it.name} className="w-12 h-12 object-contain bg-app border border-white/10" />
                  <div className="flex-1 min-w-0">
                    <div className="font-head text-sm uppercase truncate text-slate-200">{it.name}</div>
                    <div className="font-mono text-[9px] text-slate-500">{it.size} × {it.qty}</div>
                  </div>
                  <div className="font-mono text-sm text-cyan">${(it.price * it.qty).toFixed(2)}</div>
                </div>
              ))}
            </div>

            <div className="border-t border-cyan/15 mt-4 pt-4 space-y-2 font-mono text-xs">
              <Row label="SUBTOTAL" value={`$${(quote?.subtotal ?? subtotal).toFixed(2)}`} />
              <Row label={`SHIPPING${quote?.free_shipping ? " (FREE)" : ""}`} value={quote ? `$${quote.shipping.toFixed(2)}` : "—"} />
              <Row label={`TAX${quote ? ` (${Math.round(quote.tax_rate * 100)}%)` : ""}`} value={quote ? `$${quote.tax.toFixed(2)}` : "—"} />
              {quote?.discount > 0 && (
                <div className="flex items-center justify-between text-cyan" data-testid="summary-discount">
                  <span className="tracking-widest">POINTS DISCOUNT</span>
                  <span>− ${quote.discount.toFixed(2)}</span>
                </div>
              )}
            </div>
            <div className="border-t border-cyan/15 mt-3 pt-3 flex items-center justify-between">
              <span className="font-mono text-xs tracking-widest text-slate-400">TOTAL</span>
              <span className="font-head text-2xl font-bold text-cyan" data-testid="checkout-total">
                ${(quote?.total ?? subtotal).toFixed(2)}
              </span>
            </div>
            <div className="flex items-center gap-2 font-mono text-[9px] tracking-widest text-slate-500 mt-3">
              <MapPin className="w-3 h-3 text-cyan" /> ROUTED TO HELIOS PRINT LAB · CYPRUS
            </div>
            <div className="flex items-center gap-2 font-mono text-[9px] tracking-widest text-amber/80 mt-1.5">
              <Gem className="w-3 h-3" /> EARN +{Math.round((quote?.subtotal ?? subtotal) * 10 * (rewardsInfo?.multiplier || 1)) + (rewardsInfo?.first_purchase_bonus_available ? rewardsInfo.first_purchase_bonus : 0)} POINTS
              {rewardsInfo?.multiplier_active && <span className="text-amber"> · {rewardsInfo.multiplier}× WEEKEND</span>}
              {rewardsInfo?.first_purchase_bonus_available && <span className="text-cyan"> · +{rewardsInfo.first_purchase_bonus} FIRST ORDER</span>}
            </div>

            <button
              onClick={provider === "stripe" ? payStripe : payAmazon}
              disabled={busy || !validAddr}
              data-testid="place-order-btn"
              className="btn-hud w-full mt-5 border border-cyan text-cyan py-3.5 text-sm box-glow-cyan hover:bg-cyan hover:text-app disabled:opacity-50"
            >
              {busy ? "PROCESSING..." : provider === "stripe"
                ? `Pay $${(quote?.total ?? subtotal).toFixed(2)}`
                : "Continue with Amazon Pay"}
            </button>
            <div className="flex items-center justify-center gap-1 font-mono text-[9px] text-slate-600 mt-3">
              <Lock className="w-3 h-3" /> ENCRYPTED · STRIPE TEST MODE
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-slate-400">
      <span className="tracking-widest">{label}</span>
      <span className="text-slate-200">{value}</span>
    </div>
  );
}
